import React, { useEffect, useState, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaArrowRight } from 'react-icons/fa';
import { GetUnclaimedRequests, ClaimUnclaimedRequest, GetRegisteredHGOs } from '../../../../../Actions/RequestActions';
import { formatAmount, SuccessToast, WarningToast } from '../../../../../Util';

const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  
  try {
    const timeParts = time24.split(':');
    if (timeParts.length < 2) return time24;
    
    const hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    
    const period = hours >= 12 ? 'P.M' : 'A.M';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${hours12}:${minutes.padStart(2, '0')} ${period}`;
  } catch (error) {
    return time24;
  }
};

const UnclaimedRow = ({ request, onClaimClick, claiming, formatDateTime, formatAmount }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <tr 
      className="unclaimed-row"
      style={{ 
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
        transition: 'background-color 0.2s ease'
      }}
      onClick={() => onClaimClick(request)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <td className="tabledata-txt">
        {formatDateTime(request.date, request.time)}
      </td>
      <td className="tabledata-txt">
        <div>{request.bankName || '-'}</div>
        {request.iban && (
          <div className="small text-muted">{request.iban}</div>
        )}
      </td>
      <td className="tabledata-txt">{request.transactionId || '-'}</td>
      <td className="tabledata-txt">{request.narration || '-'}</td>
      <td className="tabledata-txt">{request.currency || '-'}</td>
      <td className="tabledata-txt">
        {formatAmount(request.amount || 0)}
      </td>
      <td className="tabledata-txt">
        <button
          className="btn btn-success btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            onClaimClick(request);
          }}
          disabled={claiming}
        >
          Claim
        </button>
      </td>
      <td className="tabledata-txt" style={{ position: 'relative', width: '40px' }}>
        <FaArrowRight 
          className="unclaimed-arrow"
          style={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
            cursor: 'pointer',
            fontSize: '18px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClaimClick(request);
          }}
        />
      </td>
    </tr>
  );
};

const ClaimModal = ({ isOpen, onClose, request, onClaim }) => {
  const [selectedHgoId, setSelectedHgoId] = useState("");
  const [hgos, setHGOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      GetRegisteredHGOs(
        setHGOs,
        setLoading,
        setError
      );
    }
  }, [isOpen]);

  const handleClaim = () => {
    if (!selectedHgoId) {
      WarningToast("Please select an HGO company");
      return;
    }
    onClaim(selectedHgoId);
    setSelectedHgoId("");
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Select HGO</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">HGO Company *</label>
              <select
                className="form-select"
                value={selectedHgoId}
                onChange={(e) => setSelectedHgoId(e.target.value)}
                disabled={loading}
              >
                <option value="" disabled hidden>
                  {loading ? "Loading HGOs..." : "Select"}
                </option>
                {hgos.map((hgo) => (
                  <option key={hgo.id} value={hgo.id}>
                    {hgo.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-success" onClick={handleClaim}>
              Claim
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TableUnclaimed = ({ currency, bankAccountId, reload = 0, setReload }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unclaimedRequests, setUnclaimedRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const fetchUnclaimedRequests = useCallback(() => {
    GetUnclaimedRequests(
      setUnclaimedRequests,
      setLoading,
      setError,
      page,
      setTotalPages,
      currency
    );
  }, [page, currency]);

  useEffect(() => {
    fetchUnclaimedRequests();
  }, [fetchUnclaimedRequests]);

  useEffect(() => {
    if (reload > 0) {
      fetchUnclaimedRequests();
    }
  }, [reload, fetchUnclaimedRequests]);

  const handleClaimClick = (request) => {
    setSelectedRequest(request);
    setClaimModalOpen(true);
  };

  const handleClaim = (hgoId) => {
    if (!selectedRequest) return;
    
    setClaiming(true);
    ClaimUnclaimedRequest(
      selectedRequest.id,
      hgoId,
      (errorMessage) => {
        setClaiming(false);
        if (errorMessage) {
          WarningToast(errorMessage);
        }
      },
      () => {
        setClaiming(false);
        setClaimModalOpen(false);
        setSelectedRequest(null);
        setPage(1);
        fetchUnclaimedRequests();
        if (setReload) {
          setReload((prev) => prev + 1);
        }
      }
    );
  };

  const formatDateTime = (date, time) => {
    if (!date) return '';
    const [year, month, day] = date.split('T')[0].split('-');
    const formattedDate = `${day}/${month}/${year}`;
    const formattedTime = time ? formatTimeTo12Hour(time) : '';
    return `${formattedDate}${formattedTime ? ` (${formattedTime})` : ''}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center loading-container">
        <Spinner animation="border" className="spinner-green" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">{error}</p>;
  }

  return (
    <div>
      <ClaimModal
        isOpen={claimModalOpen}
        onClose={() => {
          setClaimModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onClaim={handleClaim}
      />
      
      <div className="table-responsive">
        <table className="table table-sm table-borderless">
          <thead>
            <tr>
              <th className="tableheader-txt">Date / Time</th>
              <th className="tableheader-txt">Monazam Account</th>
              <th className="tableheader-txt">Transaction ID</th>
              <th className="tableheader-txt">Narration</th>
              <th className="tableheader-txt">Currency</th>
              <th className="tableheader-txt">Amount</th>
              <th className="tableheader-txt">Actions</th>
              <th className="tableheader-txt" style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {unclaimedRequests.length === 0 ? (
              <tr>
                <td colSpan="8" className="tabledata-txt text-center py-5 text-muted">
                  No unclaimed requests available
                </td>
              </tr>
            ) : (
              unclaimedRequests.map((request) => (
                <UnclaimedRow
                  key={request.id}
                  request={request}
                  onClaimClick={handleClaimClick}
                  claiming={claiming}
                  formatDateTime={formatDateTime}
                  formatAmount={formatAmount}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-end mt-3">
          <nav aria-label="Page navigation">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  &laquo;
                </button>
              </li>
              {[...Array(totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${page === pageNum ? 'active' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                } else if (
                  pageNum === page - 2 ||
                  pageNum === page + 2
                ) {
                  return (
                    <li key={pageNum} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
                return null;
              })}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default TableUnclaimed;
